<script setup lang="ts">
import {
    ref, computed, nextTick,
} from 'vue';
import { BBtn, BIcon } from '@/design-system/components/base';
import { useTranslation } from '@/composables';

type PropsType = {
    modelValue?: unknown[];
    isActive?: boolean;
    showClearAll?: boolean;
    clearAllIcon?: string;
    clearAllTooltip?: string;
    isFilterContainer?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    modelValue: () => [],
    showClearAll: true,
    isActive: false,
    isFilterContainer: false,
    clearAllIcon: 'mdi-close-circle',
    clearAllTooltip: 'Clear all',
});

const emit = defineEmits<{
    'update:modelValue': [value: unknown[]];
    clearAll: [];
    click: [];
    deleteKeyPressed: [];
}>();

const containerRef = ref<HTMLElement>();
const hasHorizontalOverflow = ref(false);
const isActive = ref(props.isActive);
const { t } = useTranslation();

const checkForOverflow = async () => {
    await nextTick();

    if (!containerRef.value) {
        return;
    }

    const container = containerRef.value;
    hasHorizontalOverflow.value = container.scrollWidth > container.clientWidth;
};

const scrollToActiveInput = async () => {
    await nextTick();

    if (!containerRef.value) {
        return;
    }

    const activeInputElement = containerRef.value.querySelector('.editable-chip-input');
    if (activeInputElement) {
        const container = containerRef.value;
        const inputRect = activeInputElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const inputRightRelativeToContainer = inputRect.right - containerRect.left + container.scrollLeft;
        const targetScrollPosition = inputRightRelativeToContainer + 100 - containerRect.width;

        container.scrollTo({
            left: Math.max(0, targetScrollPosition),
            behavior: 'smooth',
        });
    }
};

const handleClearAll = () => {
    emit('update:modelValue', []);
    emit('clearAll');
};

const showClearButton = computed(() => {
    return props.showClearAll && props.modelValue && props.modelValue.length > 0;
});

const handleClick = (event: MouseEvent) => {
    isActive.value = true;

    const target = event.target as HTMLElement | null;
    const clickedInteractive = target?.closest('input, textarea, button, [role="button"], .v-chip, .clear-all-btn');
    if (!clickedInteractive && containerRef.value) {
        const input = containerRef.value.querySelector<HTMLInputElement>('input:not([disabled])');
        input?.focus();
    }

    emit('click');
};

const handleDeleteKey = () => {
    emit('deleteKeyPressed');
};

defineExpose({
    checkForOverflow,
    scrollToActiveInput,
});
</script>

<template>
    <div
        ref="containerRef"
        class="chip-input-container d-flex flex-nowrap align-center ga-1 w-100 pl-2 overflow-x-auto position-relative"
        :class="{ 'has-overflow': hasHorizontalOverflow, 'is-filter-container': props.isFilterContainer }"
        @click="handleClick"
        @keydown.delete="handleDeleteKey"
    >
        <div
            v-if="props.isFilterContainer"
            class="mx-1"
        >
            <BIcon
                icon="mdi-magnify"
                color="grey-400"
                size="default"
            />
        </div>

        <slot name="placeholder">
            <span
                v-if="props.modelValue.length === 0 && !isActive"
                class="font-italic opacity-40 position-absolute pl-2"
            >
                {{ t('hooks.conditions.clickForSuggestions') }}
            </span>
        </slot>

        <slot />

        <div class="clear-all-btn d-flex align-center justify-center">
            <slot
                name="clear-button"
                :on-clear="handleClearAll"
                :show="showClearButton"
            >
                <BBtn
                    v-if="showClearButton"
                    color="transparent"
                    icon
                    variant="text"
                    density="compact"
                    :title="clearAllTooltip"
                    @click="handleClearAll"
                >
                    <BIcon
                        :icon="clearAllIcon"
                        color="grey-300"
                        size="small"
                    />
                </BBtn>
            </slot>
        </div>
    </div>
</template>

<style lang="scss" scoped>
$chip-input-container-border-radius: 15px;

.chip-input-container {
    background-color: rgb(var(--v-theme-grey-200));
    border: 1px solid rgb(var(--v-theme-grey-300));
    border-radius: $chip-input-container-border-radius;
    min-height: 40px;
    transition: padding-right 100ms ease;
    cursor: text;

    &.has-overflow {
        padding-right: 48px;
    }
}

.chip-input-container.is-filter-container {
    background-color: transparent;
    min-height: 34px;
}

.clear-all-btn {
    position: sticky;
    right: 0;
    background-color: rgb(var(--v-theme-grey-200));
    z-index: 2;
    margin-left: auto;
    padding-inline: 4px;
    border-top-right-radius: $chip-input-container-border-radius;
    border-bottom-right-radius: $chip-input-container-border-radius;
}

.is-filter-container .clear-all-btn {
    background-color: transparent;
}
</style>
